<template>
  <div class="report-basic-header">
    <b-navbar
      class="report-basic-nav w-100"
      :class="[{ show: show_nav }]"
      toggleable="lg"
      fixed="top"
      :style="[
        (show_nav || isMobileViewport) && theme_option
          ? { backgroundColor: theme_option.nav_bar_color }
          : { backgroundColor: 'rgb(255 255 255 / 0%)' }
      ]"
    >
      <b-navbar-brand v-if="selected_report?.logo_path !== null" class="mr-2">
        <b-img
          v-if="!theme_option.is_dark_option"
          :src="$store.state.static_host + selected_report?.logo_path"
          class="report-logo-xs"
          :style="{ 'max-height': '40px' }"
        />
        <b-img
          v-if="theme_option.is_dark_option && dark_logo_url !== null"
          :src="dark_logo_url"
          class="report-logo-regular"
          :style="{ 'max-height': '40px' }"
        />
      </b-navbar-brand>

      <div
        class="report-basic-name ml-2 ml-lg-4 d-none d-lg-block"
        :class="[{ show: content_list_open }]"
      >
        {{ lang !== 'ENG' ? selected_report.report_name : selected_report.report_name_e }}
      </div>

      <b-navbar-toggle target="report-basic-nav-collapse" class="ml-auto" />

      <b-collapse
        id="report-basic-nav-collapse"
        v-model="navOpen"
        is-nav
        class="report-basic-nav-collapse"
      >
        <b-navbar-nav class="report-basic-nav-items ml-lg-auto">
          <b-button-group class="report-basic-table-group mr-lg-3">
            <b-button
              v-if="active_gri_table"
              :variant="show_gri_table ? 'dark-border-selected' : 'dark-border'"
              size="md"
              :disabled="show_gri_table"
              @click="handleTableClick('GRI')"
            >
              GRI
            </b-button>
            <b-button
              v-if="active_sasb_table"
              :variant="show_sasb_table ? 'dark-border-selected' : 'dark-border'"
              size="md"
              :disabled="show_sasb_table"
              @click="handleTableClick('SASB')"
            >
              SASB
            </b-button>
            <b-button
              v-if="active_tcfd_table"
              :variant="show_tcfd_table ? 'dark-border-selected' : 'dark-border'"
              size="md"
              :disabled="show_tcfd_table"
              @click="handleTableClick('TCFD')"
            >
              TCFD
            </b-button>
            <b-button
              v-if="active_dc_table"
              :variant="show_dc_table ? 'dark-border-selected' : 'dark-border'"
              size="md"
              :disabled="show_dc_table"
              @click="handleTableClick('DATA_CENTER')"
            >
              <div class="f-100 d-inline">Data Center</div>
            </b-button>
          </b-button-group>

          <div class="report-basic-tool-group d-flex">
            <span
              v-if="showPrintButton"
              v-b-tooltip.hover
              :title="pdfReadyTooltip"
              class="d-inline-block"
            >
              <b-button
                :disabled="!pdfReady || isPrinting"
                variant="dark-border"
                size="lg"
                class="p-0 mr-2 preview-tool-btn"
                :style="{ 'pointer-events': !pdfReady || isPrinting ? 'none' : 'auto' }"
                @click="handlePrintClick"
              >
                <b-spinner v-if="isPrinting" small />
                <fa v-else icon="print" />
              </b-button>
            </span>
            <span
              v-if="theme_option.show_translation_btn"
              v-b-tooltip.hover
              :title="translateBtnTooltip"
              class="d-inline-block"
            >
              <b-button
                :disabled="!pdfReady || isLangSwitching"
                variant="dark-border"
                size="lg"
                class="p-0 mr-2 preview-tool-btn"
                :style="{
                  'pointer-events': !pdfReady || isLangSwitching ? 'none' : 'auto'
                }"
                @click="toggleLang"
              >
                <b-spinner v-if="isLangSwitching" small />
                <fa v-else icon="language" />
              </b-button>
            </span>
            <b-button
              v-if="!is_viewer"
              v-b-tooltip.hover
              variant="dark-border"
              size="lg"
              class="p-0 mr-2 preview-tool-btn"
              :title="lang != 'ENG' ? '미리보기 닫기' : 'Close preview'"
              @click="$router.push({ name: 'CheckReport' })"
            >
              <fa icon="times" />
            </b-button>
          </div>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <b-modal
      v-model="loadingModalVisible"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      hide-footer
      size="sm"
      :title="loadingModalTitle"
    >
      <div class="py-2">
        <template v-if="isPrinting">
          <div class="mb-3">
            <div
              v-for="(step, i) in printStepLabels"
              :key="'print-' + i"
              class="d-flex align-items-center mb-2"
            >
              <b-icon
                v-if="printCurrentStep > i + 1"
                icon="check-circle-fill"
                variant="success"
                class="mr-2"
              />
              <b-spinner
                v-else-if="printCurrentStep === i + 1"
                small
                variant="primary"
                class="mr-2"
              />
              <b-icon v-else icon="circle" class="text-muted mr-2" />
              <span :class="{ 'text-muted': printCurrentStep < i + 1 }">{{ step }}</span>
            </div>
          </div>
          <b-progress
            :max="printTotalSteps"
            :value="printCurrentStep > printTotalSteps ? printTotalSteps : printCurrentStep"
            variant="primary"
            animated
            height="6px"
          />
        </template>
        <template v-else-if="isLangSwitching">
          <div class="mb-3">
            <div
              v-for="(step, i) in langSwitchStepLabels"
              :key="i"
              class="d-flex align-items-center mb-2"
            >
              <b-icon
                v-if="langSwitchStep > i + 1"
                icon="check-circle-fill"
                variant="success"
                class="mr-2"
              />
              <b-spinner
                v-else-if="langSwitchStep === i + 1"
                small
                variant="primary"
                class="mr-2"
              />
              <b-icon v-else icon="circle" class="text-muted mr-2" />
              <span :class="{ 'text-muted': langSwitchStep < i + 1 }">{{ step }}</span>
            </div>
          </div>
          <b-progress
            :max="3"
            :value="langSwitchStep > 3 ? 3 : langSwitchStep"
            variant="primary"
            animated
            height="6px"
            class="mb-2"
          />
          <p class="mb-0 text-center text-muted f-85">{{ langSwitchDirectionLabel }}</p>
        </template>
      </div>
    </b-modal>

    <div class="position-relative report-basic-header-body">
      <template v-if="has_background_image">
        <div
          :class="['report-basic-header-bg background-img', { blured: content_list_open }]"
          :style="main_bg_color_gradient"
        ></div>
      </template>
      <template v-else>
        <div
          :class="['report-basic-header-bg background-color', { blured: content_list_open }]"
          :style="main_bg_color_gradient"
        ></div>
      </template>

      <b-container
        class="text-center d-flex justify-content-center align-items-center flex-column position-relative report-basic-header-title"
        :class="[{ show: !content_list_open }]"
      >
        <b-row>
          <b-col cols="12">
            <div class="p-3" :style="{ color: dynamicTextColor }">
              <h1
                class="fw-600 f-290 line-height-lg"
                :style="`font-size: ${theme_option?.title_size}rem; font-family:${selected_report.font_family};`"
              >
                {{ lang !== 'ENG' ? selected_report.report_name : selected_report.report_name_e }}
              </h1>
              <h6>{{ selected_report.start_date }} ~ {{ selected_report.end_date }}</h6>
            </div>
          </b-col>
        </b-row>
      </b-container>

      <div
        v-if="show_gri_table"
        :class="[
          'report-basic-contents-list-wrapper',
          'text-right w-100',
          { collapsed: !content_list_open },
          { 'pb-5': content_list_open }
        ]"
      >
        <ContentsListBasic
          :content_list_open="content_list_open"
          :preview_mode="true"
          :web_view="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import ContentsListBasic from './ContentsListBasic.vue';
import { updateParamsFromQueryString } from '@/components/util';
import PrintSb from '@/plugins/print_plugin/printareaSb.js';
import mobileViewportMixin from '@/mixins/mobileViewportMixin';

export default {
  name: 'ViewerHeaderBasic',
  components: {
    ContentsListBasic
  },
  mixins: [mobileViewportMixin],
  props: {
    theme_option: {
      type: Object,
      default: () => {}
    },
    dark_logo_url: {
      type: String,
      default: () => null
    },
    main_bg_color_gradient: {
      type: [Object, String],
      default: () => ({})
    },
    content_list_open: {
      type: Boolean,
      default: () => false
    },
    show_nav: {
      type: Boolean,
      default: () => false
    },
    web_view: {
      type: Boolean,
      default: () => false
    },
    active_gri_table: {
      type: Boolean,
      default: () => false
    },
    active_sasb_table: {
      type: Boolean,
      default: () => false
    },
    active_tcfd_table: {
      type: Boolean,
      default: () => false
    },
    show_gri_table: {
      type: Boolean,
      default: () => false
    },
    show_sasb_table: {
      type: Boolean,
      default: () => false
    },
    show_tcfd_table: {
      type: Boolean,
      default: () => false
    },
    active_dc_table: {
      type: Boolean,
      default: () => false
    },
    show_dc_table: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      isPrinting: false,
      printCurrentStep: 0,
      printTotalSteps: 3,
      loadingModalVisible: false,
      isLangSwitching: false,
      langSwitchStep: 0,
      langSwitchFromLang: '',
      navOpen: false
    };
  },
  computed: {
    ...mapGetters(['is_viewer', 'isMobile', 'isSafari']),
    ...mapGetters('report_list', ['selected_report']),
    ...mapState('report_list', ['lang']),
    ...mapState('documents', ['document_list_loaded']),
    ...mapState('data_tables', ['table_list_loaded']),
    ...mapState('business_cases', ['business_case_loaded']),
    ...mapState('gri2021_report_view', ['report_contents_loaded']),
    ...mapState('assurance', ['assurance_list_loaded']),
    pdfReady() {
      return (
        this.document_list_loaded &&
        this.table_list_loaded &&
        this.business_case_loaded &&
        this.report_contents_loaded &&
        this.assurance_list_loaded
      );
    },
    printStepLabels() {
      if (this.lang !== 'ENG') {
        return ['콘텐츠 준비', '이미지 로딩', '인쇄 준비'];
      }
      return ['Preparing content', 'Loading images', 'Preparing print'];
    },
    langSwitchStepLabels() {
      if (this.langSwitchFromLang === 'ENG') {
        return ['Checking translation data', 'Converting content', 'Rendering'];
      }
      return ['번역 데이터 확인', '콘텐츠 변환', '화면 렌더링'];
    },
    langSwitchDirectionLabel() {
      if (this.langSwitchFromLang === 'KOR') {
        return '한국어 → English';
      }
      return 'English → 한국어';
    },
    loadingModalTitle() {
      if (this.isPrinting) {
        return this.lang !== 'ENG' ? 'PDF 출력 준비중' : 'Preparing PDF';
      }
      if (this.isLangSwitching) {
        return this.langSwitchFromLang !== 'ENG' ? '언어 전환중' : 'Switching Language';
      }
      return '';
    },
    translateBtnTooltip() {
      if (!this.pdfReady) {
        return this.lang !== 'ENG' ? '보고서 준비중입니다.' : 'Preparing report...';
      }
      return this.lang !== 'ENG' ? '영문보기' : 'Korean';
    },
    pdfReadyTooltip() {
      if (this.isPrinting) {
        return this.lang !== 'ENG' ? 'pdf 출력 준비중...' : 'Preparing PDF...';
      }
      if (this.pdfReady) {
        return this.lang !== 'ENG' ? '출력하기' : 'Print to PDF';
      }
      return this.lang !== 'ENG' ? 'pdf 출력을 준비중입니다.' : 'Preparing PDF...';
    },
    dynamicTextColor() {
      if (!this.theme_option) return 'black'; // 기본값 또는 상황에 맞게 지정
      const titleColor = this.theme_option.title_color;
      if (titleColor === '#FFFFFF') {
        return this.has_background_image ? 'white' : 'black';
      } else {
        return titleColor;
      }
    },
    popTitle() {
      if (this.lang === 'KOR') {
        return this.selected_report.report_name;
      } else {
        return this.selected_report.report_name_e;
      }
    },
    main_color() {
      return this.selected_report?.main_color;
    },
    has_background_image() {
      return !!this.main_bg_color_gradient['background-image'];
    },
    // PDF 인쇄는 모바일 기기·좁은 뷰포트·Safari·샘플 보고서에서 숨긴다.
    // - isMobile (UA + touch): iPad Chrome 가로모드(≥992) 같은 큰 화면 모바일 기기까지 포함
    // - isMobileViewport (<992): 데스크탑 좁은 창
    // 둘은 서로 보완 관계라 OR로 묶어 둘 중 하나라도 모바일이면 숨김
    showPrintButton() {
      return (
        this.theme_option?.show_printing_btn &&
        !this.selected_report?.sample_report &&
        !this.isSafari &&
        !this.isMobile &&
        !this.isMobileViewport
      );
    }
  },
  created() {},
  methods: {
    ...mapMutations('report_list', ['change_lang']),

    closeNavOnMobile() {
      if (this.isMobileViewport) {
        this.navOpen = false;
      }
    },
    handleTableClick(type) {
      this.$emit('showTableView', type);
      this.closeNavOnMobile();
    },

    async toggleLang() {
      if (this.isLangSwitching) return;
      this.closeNavOnMobile();
      this.isLangSwitching = true;
      this.langSwitchStep = 0;
      this.langSwitchFromLang = this.lang;
      this.loadingModalVisible = true;

      try {
        // 모달이 화면에 paint될 때까지 대기
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await this.delay(150);

        // Step 1: 번역 데이터 확인
        this.langSwitchStep = 1;
        await this.delay(500);

        // Step 2: 콘텐츠 변환 + 언어 전환 실행
        this.langSwitchStep = 2;
        await this.delay(300);

        if (this.langSwitchFromLang === 'KOR') {
          this.change_lang('ENG');
          document.querySelector('title').textContent = this.selected_report.report_name_e;
          updateParamsFromQueryString('l', 'ENG', false);
        } else {
          this.change_lang('KOR');
          document.querySelector('title').textContent = this.selected_report.report_name;
          updateParamsFromQueryString('l', null, false);
        }

        // Step 3: 화면 렌더링
        this.langSwitchStep = 3;
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await this.delay(500);
      } catch (e) {
        // 언어 전환 실패 시 무음으로 묻히지 않도록 로깅 — handlePrintClick와 동일 패턴
        // eslint-disable-next-line no-console
        console.error('[ViewerHeaderBasic] toggleLang failed:', e);
      } finally {
        this.isLangSwitching = false;
        this.loadingModalVisible = false;
        this.langSwitchStep = 0;
      }
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async handlePrintClick() {
      if (!this.pdfReady || this.isPrinting) return;

      this.isPrinting = true;
      this.printCurrentStep = 0;
      this.loadingModalVisible = true;

      try {
        await this.$nextTick();
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        await this.delay(150);

        // Step 1: 콘텐츠 준비
        this.printCurrentStep = 1;
        await this.$nextTick();
        await this.delay(600);

        // Step 2: 이미지 로딩 대기
        this.printCurrentStep = 2;
        await this.waitForImages();
        await this.delay(600);

        // Step 3: 인쇄 준비
        this.printCurrentStep = 3;
        await this.delay(400);

        this.loadingModalVisible = false;

        new PrintSb({
          ids: '#full-report-preview',
          popTitle: this.popTitle,
          endCallback: () => {
            this.isPrinting = false;
            this.printCurrentStep = 0;
          }
        });
      } catch (e) {
        this.isPrinting = false;
        this.printCurrentStep = 0;
        this.loadingModalVisible = false;
        throw e;
      }
    },
    waitForImages() {
      return new Promise(resolve => {
        const printElement = document.querySelector('#full-report-preview');
        if (!printElement) {
          resolve();
          return;
        }
        const images = Array.from(printElement.querySelectorAll('img'));
        const allLoaded = images.length === 0 || images.every(img => img.complete);
        if (allLoaded) {
          resolve();
          return;
        }
        let loaded = 0;
        const total = images.filter(img => !img.complete).length;
        const onLoad = () => {
          loaded++;
          if (loaded >= total) resolve();
        };
        images.forEach(img => {
          if (!img.complete) {
            img.addEventListener('load', onLoad, { once: true });
            img.addEventListener('error', onLoad, { once: true });
          }
        });
        setTimeout(resolve, 10000);
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@media print {
  @page {
    margin: 0cm;
  }
}

.report-basic-header {
  &-body {
    min-height: 100vh;
    padding-top: 70px;
  }

  &-title {
    transition-property: opacity, min-height, height;
    transition-duration: 0.5s, 1s, 1s;
    transition-delay: 0s, 0s, 0s;
    transition-timing-function: ease, ease, ease;
    min-height: 170px;
    height: 0px;
    opacity: 0;

    &.show {
      transition-duration: 1s, 0.5s, 0.5s;
      transition-delay: 1s, 0s, 0s;
      min-height: 170px;
      height: calc(80vh - 69.8px);
      opacity: 1;
    }
  }

  &-bg {
    transition-property: opacity;
    transition-duration: 0.75s;
    transition-timing-function: ease;
    transition-delay: 0.5s;
    position: absolute;
    top: 0px;
    left: 0px;
    height: 100%;
    width: 100%;

    &.blured {
      transition-delay: 0s;

      &.background-img {
        opacity: 0.15 !important;
      }

      &.background-color {
        opacity: 0.35 !important;
      }
    }
  }
}

.report-basic-nav {
  transition: background-color 0.3s ease-in-out;
  z-index: 999;
}

.report-basic-name {
  transition: opacity 0.3s ease-in-out;
  opacity: 0;

  &.show {
    opacity: 1;
  }
}

// 절대 배치된 .report-basic-header-bg 위로 wrapper가 올라오도록 stacking 보장
// (없으면 모바일 등에서 wrapper가 bg 뒤로 가려져 보이지 않음)
.report-basic-contents-list-wrapper {
  position: relative;
  z-index: 1;
}

// 모바일(<lg)에서 nav 항목을 세로로 펼쳐 드롭다운 형태로 표시
@media (max-width: 991.98px) {
  .report-basic-nav-collapse {
    width: 100%;
    background-color: rgba(255, 255, 255, 0.95);
    border-radius: 0.25rem;
    margin-top: 0.5rem;
    padding: 0.75rem;
  }

  .report-basic-nav-items {
    flex-direction: column !important;
    align-items: stretch !important;
    width: 100%;

    // 모바일에서도 GRI/SASB/TCFD/Data Center 버튼은 가로 배치 (균등 너비)
    .report-basic-table-group {
      display: flex;
      flex-direction: row;
      width: 100%;
      margin-bottom: 0.5rem;

      .btn {
        flex: 1 1 0;
        border-radius: 0 !important;
        margin-right: -1px;
        margin-bottom: 0;
      }

      .btn:first-child {
        border-top-left-radius: 0.25rem !important;
        border-bottom-left-radius: 0.25rem !important;
      }

      .btn:last-child {
        border-top-right-radius: 0.25rem !important;
        border-bottom-right-radius: 0.25rem !important;
        margin-right: 0;
      }
    }

    .report-basic-tool-group {
      justify-content: flex-end;
      flex-wrap: wrap;
    }
  }
}

@media (max-width: 575.98px) {
  .report-basic-header {
    &-body {
      padding-top: 56px;
    }

    // 모바일에서 title이 뷰포트(navbar 아래)를 100% 채우도록
    &-title {
      min-height: calc(100vh - 56px);

      &.show {
        min-height: calc(100vh - 56px);
        height: calc(100vh - 56px);
      }

      h1 {
        font-size: 2rem !important;
      }
    }

    // 모바일에서 커버 사진은 title 영역(navbar 아래 ~ 뷰포트 하단)에만 표시
    // body가 ToC 등으로 더 길어지더라도 cover bg 자체를 viewport 높이로 제한
    // 너비는 디바이스 너비에 맞추지 않고, 높이를 디바이스 높이에 맞춘 뒤 가운데 정렬
    &-bg.background-img {
      top: 56px !important;
      height: calc(100vh - 56px) !important;
      background-size: auto 100% !important;
      background-position: center !important;
    }
  }
}
</style>
